// shared layout (all variations)
import { LobbyVariationItemDef, OptionsCategoryLayoutData, OptionsItemLayoutData, OptionsItemLayoutDefinitionData, SharedLayoutData, VariationLayoutData } from "../../types/types";
import { VariationId } from "../screen/TableScreen";
import { AppLocalization } from "../localization/AppLocalization";

import shared_layout from "../data/shared/shared_layout.json";
import belote_layout from "../data/belote/belote_layout.json";
import coinche_layout from "../data/coinche/coinche_layout.json";
import contree_layout from "../data/contree/contree_layout.json";

import itemDefs from "../data/shared/options_layout_defs.json";


export enum OptionsItemKeys {
    applicationLanguage = "applicationLanguage",
    cardDeck = "cardDeck",
    cardBack = "cardBack",
    background = "background",
    musicTrack = "musicTrack",
    graphicsQuality = "graphicsQuality",
    gameSpeed = "gameSpeed",
    directionOfPlay = "directionOfPlay",
    cardsSorting = "cardsSorting",
    avoidSameCardColorNeighbour = "avoidSameCardColorNeighbour",
    animateDealingCards = "animateDealingCards",
    showScores = "showScores",
    /**
     * Makes 'TIPS' Table UI available. 
     * Funtionally, tip cards are visually highlighted (while rest of cards is grayed out) 
     * and made interactive (while rest is disabled).
     * 
     * Showing of tips follows following rules:
     * A) if 'autoShowGameTips' is ON, automatically shows a game tip on player's turn.
     * B) if 'autoShowGameTips' is OFF, to display a tip once its player's turn, 
     *    Player has to manually click the HINT button 'every time' its their turn.
     */
    enableGameTips = "enableGameTips",
    autoShowGameTips = "autoShowGameTips",
    autoPlay = "autoPlay",
    autoCollectTricks = "autoCollectTricks",
    autoCollectDeclarations = "autoCollectDeclarations",
    /**
     * Applies to hand cards of live layer only (not bots)
     * ON: Playable cards 'pop up' visually above other cards on vertical axis.
     * OFF: Playable cards do not pop up above unplayable cards.
     */
    popupPlayableCards = "popupPlayableCards",
    pointsPerGame = "pointsPerGame",
    playWithDeclarations = "playWithDeclarations",
    enableNTAT = "enableNTAT",
    beloteRebeloteAtAllTrumps = "beloteRebeloteAtAllTrumps",
    pissTrump = "pissTrump",
    forceTrumpPlay = "forceTrumpPlay",
    /**
     * True: during the bidding phase, if all players have passed two times, the dealer has to choose a trump suit.
     */
    variantVache = "variantVache",
    passOnJack = "passOnJack",
    allowGenerale = "allowGenerale",
    // NOTE: can not have allowSurCoinche & allowReDouble together, because Options Item Localization is tied to it, and it needs to differ for each item.
    allowSurCoinche = "allowSurCoinche",
    allowReDouble = "allowReDouble",
    aiLevel = "aiLevel",
    declarationsCountForContractWinLoss = "declarationsCountForContractWinLoss",
    beloteCountsForContractWinLoss = "beloteCountsForContractWinLoss",
    /**
     * When you loose contract (other team gets more points), you normally loose the table points you earned (except declarations and belote). 
     * With this ON, you loose both the points from table and declarations points.
     */
    declarationLossOnContractLossOrCapot = "declarationLossOnContractLossOrCapot",
    hangingPoints = "hangingPoints",
    ntatDoublePoints = "ntatDoublePoints",
    ntPoints130 = "ntPoints130",
    atPoints258 = "atPoints258",
    contractValueSufficientForAttackWin = "contractValueSufficientForAttackWin",
    scoreContractOnly = "scoreContractOnly",
    coincheDoublesAllPoints = "coincheDoublesAllPoints",
    doubleDoublesAllPoints = "doubleDoublesAllPoints",
    resetOptionsBelote = "resetOptionsBelote",
    resetStatsBelote = "resetStatsBelote",
    resetOptionsCoinche = "resetOptionsCoinche",
    resetStatsCoinche = "resetStatsCoinche",
    resetOptionsContree = "resetOptionsContree",
    resetStatsContree = "resetStatsContree",
}

/**
 * Layout manager
 * Keeps refs to:
 * - ItemDefinitionsData - layout definition for each item, by key
 * - whole options layout, which gives (structured by category) access to item definition keys
 */
export class LayoutDataManager {
    public static variationData: VariationLayoutData[];
    private static unsupportedVariationItemKeys: string[][];

    public static Init(): void {
        this.variationData = [];
        this.variationData[VariationId.BELOTE] = belote_layout;
        this.variationData[VariationId.COINCHE] = coinche_layout;
        this.variationData[VariationId.CONTREE] = contree_layout;

        this.unsupportedVariationItemKeys = [];
        this.unsupportedVariationItemKeys[VariationId.BELOTE] = [
            // although disallowed, this could be implemented, but will require variationId check in our generic score counting system (because in Belote it is counted differently)
            OptionsItemKeys.allowGenerale,
            OptionsItemKeys.allowSurCoinche,
            OptionsItemKeys.allowReDouble,
            OptionsItemKeys.declarationsCountForContractWinLoss,
            OptionsItemKeys.declarationLossOnContractLossOrCapot,
            OptionsItemKeys.contractValueSufficientForAttackWin,
            OptionsItemKeys.scoreContractOnly,
            OptionsItemKeys.coincheDoublesAllPoints,
            OptionsItemKeys.doubleDoublesAllPoints,
        ];
        this.unsupportedVariationItemKeys[VariationId.COINCHE] = [
            OptionsItemKeys.variantVache,
            OptionsItemKeys.passOnJack,
            OptionsItemKeys.allowReDouble,
            OptionsItemKeys.hangingPoints,
            OptionsItemKeys.ntatDoublePoints,
            OptionsItemKeys.ntPoints130,
            OptionsItemKeys.atPoints258,
            OptionsItemKeys.doubleDoublesAllPoints,
        ];
        this.unsupportedVariationItemKeys[VariationId.CONTREE] = [
            OptionsItemKeys.variantVache,
            OptionsItemKeys.passOnJack,
            OptionsItemKeys.allowSurCoinche,
            OptionsItemKeys.declarationsCountForContractWinLoss,
            OptionsItemKeys.hangingPoints,
            OptionsItemKeys.ntatDoublePoints,
            OptionsItemKeys.ntPoints130,
            OptionsItemKeys.atPoints258,
            OptionsItemKeys.coincheDoublesAllPoints,
        ];


        // console.log(this.unsupportedVariationItemKeys);

        // console error to report unsupported item IDs by specific variations.
        this.ReportUnsupportedVariationItemIDsUses();
    }

    public static GetSharedData(): SharedLayoutData {
        return shared_layout;
    }

    public static GetVariationData(variationId?: VariationId): VariationLayoutData {
        const curVariationId: VariationId = variationId ?? AppLocalization.GetCurVariationId();
        switch (curVariationId) {
            case VariationId.BELOTE:
                return belote_layout;
            case VariationId.COINCHE:
                return coinche_layout;
            case VariationId.CONTREE:
                return contree_layout;
        }
    }

    /**
     * Checks if option/layout item exists for variation
     * @param key 
     * @param variationId 
     */
    public static HasVariationKey(key: string, variationId: VariationId): boolean {
        const variationLayoutData = this.GetVariationData(variationId);
        const categories = variationLayoutData.options.categories;
        for (let i = 0; i < categories.length; i++) {
            const categoryItems = categories[i].items;
            for (let j = 0; j < categoryItems.length; j++) {
                if (categoryItems[j].id == key) {
                    return true;
                }
            }
        }
        return false;
    }

    /**
    Example:
        "applicationLanguage":
        {
            "itemType": 2,
            "contentType": 1,
        }
     * @param id item def. key for ex: "applicationLanguage"
     * @returns item def for ex: { "itemType": 2, "contentType": 1 }
     */
    public static GetItemDefinitionData(id: string): OptionsItemLayoutDefinitionData {
        return itemDefs[id];
    }

    /**
     * Sweeps layout IDs used in variations, and uses console.error to report use of unsupported IDs in varatioans, if detects any. 
     */
    public static ReportUnsupportedVariationItemIDsUses(): void {
        // report improper use of 'scoreContractOnly' in VariationId.BELOTE
        // TODO: use generic system to assign unsupported itemIds for variationIds
        // for each variationId
        for (let i = 0; i < this.unsupportedVariationItemKeys.length; i++) {
            const variationItems: string[] = this.unsupportedVariationItemKeys[i];
            if (variationItems) {
                // for each option itemId
                for (let j = 0; j < variationItems.length; j++) {
                    const itemId: string = variationItems[j];
                    const cats: OptionsCategoryLayoutData[] = this.variationData[i].options.categories;
                    // for each options category
                    for (let k = 0; k < cats.length; k++) {
                        const cat: OptionsCategoryLayoutData = cats[k];
                        // for each item inside category
                        for (let l = 0; l < cat.items.length; l++) {
                            const layoutItemData: OptionsItemLayoutData = cat.items[l];
                            if (layoutItemData.id == itemId) {
                                console.error(`ERROR: Please remove '${itemId}' ID from '${VariationId[i]}' layout JSON, it is not supported by that variation.`);
                            }
                        }
                    }
                }
            }
        }
    }

    public static GetLobbyVariationItemIndexByVariationId(variationId: VariationId): number {
        const itemsDefs: LobbyVariationItemDef[] = this.GetSharedData().lobby.items;
        for (let i = 0; i < itemsDefs.length; i++) {
            if (itemsDefs[i].variationId == variationId) {
                return i;
            }
        }
        return 0;
    }

    public static GetVariationIdByLobbyVariationItemIndex(lobbyItemIndex: number): VariationId {
        const lobbyItems: LobbyVariationItemDef[] = this.GetSharedData().lobby.items;
        const lobbyItemIndexFixed = Math.min(Math.max(lobbyItemIndex, 0), lobbyItems.length-1)
        return lobbyItems[lobbyItemIndexFixed].variationId;
    }

    /**
     * Item index of LobbyVariationItem that is selected by default in Lobby
     * Used by GameScene and LobbyScene
     * Really only used in SINGLE Lobby Layout mode, since Lobby otherwise (fresh start)
     * switches to MULTI Layout mode, however GameScene will still use this to initialize with default data (so it is still useful)
     */
    public static GetDefaultItemIndex(): number {
        return this.GetSharedData().lobby.defaultItemIndex;
    }
}